import React from 'react'
import { Box, Cylinder, Sphere } from '@react-three/drei'
import { ADUEquipmentModel as ADUEquipmentType } from '../../../store/designStore'

interface ADUEquipmentModelProps extends ADUEquipmentType {
  onClick?: () => void
  onPointerDown?: (event: any) => void
  isSelected?: boolean
  isDragging?: boolean
}

const ADUEquipmentModel: React.FC<ADUEquipmentModelProps> = ({ 
  position, 
  rotation, 
  scale, 
  equipmentType,
  dimensions,
  materials,
  onClick,
  onPointerDown,
  isSelected = false,
  isDragging = false
}) => {
  
  const renderEquipmentShape = () => {
    const { width, height, depth } = dimensions
    
    switch (equipmentType) {
      // PLUMBING FIXTURES
      case 'toilet':
        // Toilet: Tank and bowl
        return (
          <group>
            {/* Toilet bowl */}
            <Cylinder args={[width/2, width/2 * 0.8, height * 0.4, 16]} position={[0, -height * 0.3, depth * 0.1]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" roughness={0.1} metalness={0.1} />
            </Cylinder>
            {/* Toilet tank */}
            <Box args={[width * 0.7, height * 0.5, depth * 0.6]} position={[0, height * 0.1, -depth * 0.2]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" roughness={0.1} metalness={0.1} />
            </Box>
            {/* Toilet seat */}
            <Cylinder args={[width/2 * 0.9, width/2 * 0.9, 0.05, 16]} position={[0, height * 0.1, depth * 0.1]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            {/* Flush handle */}
            <Box args={[0.1, 0.2, 0.05]} position={[-width * 0.3, height * 0.1, -depth * 0.2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.8} />
            </Box>
          </group>
        )

      case 'sink_bathroom':
        // Bathroom sink with faucet
        return (
          <group>
            {/* Sink basin */}
            <Cylinder args={[width/2, width/2 * 0.8, height * 0.3, 16]} position={[0, -height * 0.1, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" roughness={0.1} metalness={0.1} />
            </Cylinder>
            {/* Faucet base */}
            <Cylinder args={[0.1, 0.1, height * 0.3, 8]} position={[0, height * 0.15, -width * 0.2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} roughness={0.1} />
            </Cylinder>
            {/* Faucet spout */}
            <Cylinder args={[0.05, 0.05, width * 0.4, 8]} position={[0, height * 0.25, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} roughness={0.1} />
            </Cylinder>
            {/* Handles */}
            <Sphere args={[0.08]} position={[-0.15, height * 0.2, -width * 0.2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Sphere>
            <Sphere args={[0.08]} position={[0.15, height * 0.2, -width * 0.2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Sphere>
          </group>
        )

      case 'sink_kitchen':
        // Kitchen sink - larger with double basin
        return (
          <group>
            {/* Left basin */}
            <Box args={[width * 0.45, height * 0.4, depth * 0.8]} position={[-width * 0.25, -height * 0.1, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" roughness={0.1} metalness={0.3} />
            </Box>
            {/* Right basin */}
            <Box args={[width * 0.45, height * 0.4, depth * 0.8]} position={[width * 0.25, -height * 0.1, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" roughness={0.1} metalness={0.3} />
            </Box>
            {/* Faucet */}
            <Cylinder args={[0.08, 0.08, height * 0.4, 8]} position={[0, height * 0.2, -depth * 0.2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} roughness={0.1} />
            </Cylinder>
            {/* Spout */}
            <Cylinder args={[0.04, 0.04, width * 0.3, 8]} position={[0, height * 0.3, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Cylinder>
          </group>
        )

      case 'shower':
        // Shower stall
        return (
          <group>
            {/* Shower base */}
            <Box args={[width, 0.1, depth]} position={[0, -height/2 + 0.05, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Box>
            {/* Shower walls (3 sides) */}
            <Box args={[0.1, height, depth]} position={[-width/2, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" transparent opacity={0.7} />
            </Box>
            <Box args={[0.1, height, depth]} position={[width/2, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" transparent opacity={0.7} />
            </Box>
            <Box args={[width, height, 0.1]} position={[0, 0, -depth/2]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" transparent opacity={0.7} />
            </Box>
            {/* Shower head */}
            <Cylinder args={[0.15, 0.15, 0.1, 8]} position={[0, height * 0.4, -depth * 0.4]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Cylinder>
            {/* Shower controls */}
            <Cylinder args={[0.1, 0.1, 0.1, 8]} position={[-width * 0.3, height * 0.1, -depth * 0.4]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Cylinder>
          </group>
        )

      case 'bathtub':
        // Bathtub
        return (
          <group>
            {/* Tub body */}
            <Box args={[width, height * 0.6, depth]} position={[0, -height * 0.2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" roughness={0.1} />
            </Box>
            {/* Tub interior */}
            <Box args={[width * 0.9, height * 0.5, depth * 0.9]} position={[0, -height * 0.15, 0]} castShadow>
              <meshStandardMaterial color="#ffffff" roughness={0.05} />
            </Box>
            {/* Faucet */}
            <Cylinder args={[0.08, 0.08, 0.3, 8]} position={[0, height * 0.1, depth * 0.4]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.9} />
            </Cylinder>
            {/* Drain */}
            <Cylinder args={[0.08, 0.08, 0.05, 8]} position={[width * 0.3, -height * 0.15, -depth * 0.3]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
          </group>
        )

      // KITCHEN APPLIANCES
      case 'refrigerator':
        // Refrigerator
        return (
          <group>
            {/* Main body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" metalness={0.3} roughness={0.2} />
            </Box>
            {/* Freezer door */}
            <Box args={[width * 0.95, height * 0.3, 0.05]} position={[0, height * 0.25, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#cbd5e0" />
            </Box>
            {/* Main door */}
            <Box args={[width * 0.95, height * 0.65, 0.05]} position={[0, -height * 0.1, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#cbd5e0" />
            </Box>
            {/* Handles */}
            <Box args={[0.05, 0.3, 0.1]} position={[width * 0.4, height * 0.25, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Box>
            <Box args={[0.05, 0.4, 0.1]} position={[width * 0.4, -height * 0.1, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Box>
          </group>
        )

      case 'stove':
        // Kitchen stove/range
        return (
          <group>
            {/* Main body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.6} roughness={0.3} />
            </Box>
            {/* Cooktop */}
            <Box args={[width * 0.9, 0.05, depth * 0.6]} position={[0, height/2 + 0.025, depth * 0.1]} castShadow>
              <meshStandardMaterial color="#1a202c" metalness={0.8} roughness={0.1} />
            </Box>
            {/* Burners */}
            {Array.from({ length: 4 }, (_, i) => (
              <Cylinder 
                key={i} 
                args={[0.15, 0.15, 0.05, 16]} 
                position={[
                  (i % 2 === 0 ? -width * 0.25 : width * 0.25),
                  height/2 + 0.05,
                  (i < 2 ? depth * 0.25 : -depth * 0.05)
                ]} 
                castShadow
              >
                <meshStandardMaterial color="#4a5568" />
              </Cylinder>
            ))}
            {/* Oven door */}
            <Box args={[width * 0.85, height * 0.6, 0.05]} position={[0, -height * 0.1, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Box>
            {/* Oven handle */}
            <Cylinder args={[0.03, 0.03, width * 0.6, 8]} position={[0, height * 0.1, depth/2 + 0.05]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Cylinder>
          </group>
        )

      case 'dishwasher':
        // Dishwasher
        return (
          <group>
            {/* Main body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.4} roughness={0.4} />
            </Box>
            {/* Front panel */}
            <Box args={[width * 0.95, height * 0.9, 0.05]} position={[0, 0, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Control panel */}
            <Box args={[width * 0.8, 0.15, 0.05]} position={[0, height * 0.4, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            {/* Handle */}
            <Cylinder args={[0.02, 0.02, width * 0.6, 8]} position={[0, height * 0.2, depth/2 + 0.05]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#718096" metalness={0.8} />
            </Cylinder>
          </group>
        )

      // CABINETS & STORAGE
      case 'kitchen_cabinets':
        // Kitchen cabinet unit
        return (
          <group>
            {/* Cabinet box */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#8b4513" roughness={0.6} />
            </Box>
            {/* Cabinet doors */}
            <Box args={[width * 0.48, height * 0.9, 0.05]} position={[-width * 0.25, 0, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#a0522d" roughness={0.5} />
            </Box>
            <Box args={[width * 0.48, height * 0.9, 0.05]} position={[width * 0.25, 0, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#a0522d" roughness={0.5} />
            </Box>
            {/* Handles */}
            <Cylinder args={[0.02, 0.02, 0.15, 8]} position={[-width * 0.35, 0, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Cylinder>
            <Cylinder args={[0.02, 0.02, 0.15, 8]} position={[width * 0.35, 0, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Cylinder>
          </group>
        )

      case 'bathroom_vanity':
        // Bathroom vanity with sink
        return (
          <group>
            {/* Vanity cabinet */}
            <Box args={[width, height * 0.7, depth]} position={[0, -height * 0.15, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#8b4513" roughness={0.6} />
            </Box>
            {/* Countertop */}
            <Box args={[width * 1.05, 0.1, depth * 1.05]} position={[0, height * 0.2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" roughness={0.1} />
            </Box>
            {/* Sink basin */}
            <Cylinder args={[width * 0.2, width * 0.18, 0.15, 16]} position={[0, height * 0.15, 0]} castShadow>
              <meshStandardMaterial color="#ffffff" roughness={0.05} />
            </Cylinder>
            {/* Cabinet doors */}
            <Box args={[width * 0.45, height * 0.6, 0.05]} position={[-width * 0.25, -height * 0.15, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#a0522d" />
            </Box>
            <Box args={[width * 0.45, height * 0.6, 0.05]} position={[width * 0.25, -height * 0.15, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#a0522d" />
            </Box>
          </group>
        )

      // WINDOWS & DOORS
      case 'window_standard':
        // Standard window
        return (
          <group>
            {/* Window frame */}
            <Box args={[width, height, 0.2]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Box>
            {/* Glass panes */}
            <Box args={[width * 0.9, height * 0.45, 0.05]} position={[0, height * 0.25, 0]} castShadow>
              <meshStandardMaterial color="#e6fffa" transparent opacity={0.3} />
            </Box>
            <Box args={[width * 0.9, height * 0.45, 0.05]} position={[0, -height * 0.25, 0]} castShadow>
              <meshStandardMaterial color="#e6fffa" transparent opacity={0.3} />
            </Box>
            {/* Window sash */}
            <Box args={[width * 0.95, 0.05, 0.1]} position={[0, 0, 0]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
          </group>
        )

      case 'entry_door':
        // Entry door
        return (
          <group>
            {/* Door frame */}
            <Box args={[width * 1.1, height * 1.05, 0.3]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Box>
            {/* Door */}
            <Box args={[width, height, 0.1]} position={[0, 0, 0.1]} castShadow receiveShadow>
              <meshStandardMaterial color="#8b4513" roughness={0.6} />
            </Box>
            {/* Door panels */}
            <Box args={[width * 0.35, height * 0.4, 0.05]} position={[0, height * 0.2, 0.125]} castShadow>
              <meshStandardMaterial color="#a0522d" />
            </Box>
            <Box args={[width * 0.35, height * 0.4, 0.05]} position={[0, -height * 0.2, 0.125]} castShadow>
              <meshStandardMaterial color="#a0522d" />
            </Box>
            {/* Door handle */}
            <Sphere args={[0.08]} position={[width * 0.35, 0, 0.15]} castShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} />
            </Sphere>
          </group>
        )

      // HVAC & UTILITIES
      case 'hvac_unit':
        // HVAC unit: Large rectangular unit with fan and coils
        return (
          <group>
            {/* Main HVAC body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.6} roughness={0.4} />
            </Box>
            {/* Fan grille */}
            <Cylinder args={[width * 0.35, width * 0.35, 0.2, 16]} position={[0, height/2 + 0.1, 0]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            {/* Fan blades */}
            {Array.from({ length: 4 }, (_, i) => (
              <Box 
                key={i} 
                args={[width * 0.3, 0.05, 0.3]} 
                position={[0, height/2 + 0.15, 0]} 
                rotation={[0, (i * Math.PI) / 2, 0]}
                castShadow
              >
                <meshStandardMaterial color="#718096" />
              </Box>
            ))}
            {/* Refrigerant lines */}
            <Cylinder args={[0.1, 0.1, width * 0.6, 8]} position={[-width * 0.4, -height * 0.3, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#f6ad55" />
            </Cylinder>
            <Cylinder args={[0.15, 0.15, width * 0.6, 8]} position={[-width * 0.4, -height * 0.1, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#4299e1" />
            </Cylinder>
          </group>
        )

      case 'water_heater':
        // Water heater: Cylindrical tank with connections
        return (
          <group>
            {/* Main tank */}
            <Cylinder args={[width/2, width/2, height, 16]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" metalness={0.1} roughness={0.8} />
            </Cylinder>
            {/* Insulation jacket */}
            <Cylinder args={[width/2 + 0.1, width/2 + 0.1, height * 0.8, 16]} position={[0, 0, 0]} castShadow>
              <meshStandardMaterial color="#f7fafc" transparent opacity={0.8} />
            </Cylinder>
            {/* Control valve */}
            <Box args={[0.3, 0.3, 0.3]} position={[0, height/2 - 0.5, width/2]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Water connections */}
            <Cylinder args={[0.1, 0.1, 0.8, 8]} position={[width/3, height/2 - 0.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#4299e1" />
            </Cylinder>
            <Cylinder args={[0.1, 0.1, 0.8, 8]} position={[-width/3, height/2 - 0.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#e53e3e" />
            </Cylinder>
          </group>
        )

      // LIGHTING & ELECTRICAL
      case 'ceiling_light':
        // Ceiling light fixture
        return (
          <group>
            {/* Ceiling mount */}
            <Cylinder args={[0.2, 0.2, 0.1, 16]} position={[0, height/2 - 0.05, 0]} castShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Cylinder>
            {/* Light fixture */}
            <Cylinder args={[width/2, width/2 * 0.8, height * 0.3, 16]} position={[0, height * 0.15, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" transparent opacity={0.8} />
            </Cylinder>
            {/* Light bulb */}
            <Sphere args={[0.15]} position={[0, 0, 0]} castShadow>
              <meshStandardMaterial color="#fff2cc" emissive="#fff2cc" emissiveIntensity={0.3} />
            </Sphere>
          </group>
        )

      case 'recessed_light':
        // Recessed ceiling light
        return (
          <group>
            {/* Housing */}
            <Cylinder args={[width/2, width/2 * 0.9, height, 16]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            {/* Trim ring */}
            <Cylinder args={[width/2 + 0.05, width/2 + 0.05, 0.05, 16]} position={[0, height/2 - 0.025, 0]} castShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Cylinder>
            {/* LED bulb */}
            <Cylinder args={[width/2 * 0.7, width/2 * 0.7, 0.1, 16]} position={[0, height/2 - 0.05, 0]} castShadow>
              <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.4} />
            </Cylinder>
          </group>
        )

      case 'outlet':
        // Electrical outlet
        return (
          <group>
            {/* Outlet plate */}
            <Box args={[width, height, 0.05]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Box>
            {/* Outlet holes */}
            <Box args={[0.1, 0.05, 0.02]} position={[-0.1, 0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            <Box args={[0.1, 0.05, 0.02]} position={[0.1, 0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            <Box args={[0.05, 0.05, 0.02]} position={[0, 0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            {/* Lower outlet */}
            <Box args={[0.1, 0.05, 0.02]} position={[-0.1, -0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            <Box args={[0.1, 0.05, 0.02]} position={[0.1, -0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            <Box args={[0.05, 0.05, 0.02]} position={[0, -0.1, 0.025]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
          </group>
        )
      
      case 'electrical_panel':
        // Main electrical panel: Wall-mounted service panel
        return (
          <group>
            {/* Main panel box */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.8} roughness={0.2} />
            </Box>
            {/* Panel door */}
            <Box args={[width * 0.9, height * 0.9, 0.05]} position={[0, 0, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Meter */}
            <Cylinder args={[0.3, 0.3, 0.2, 16]} position={[0, height * 0.3, depth/2 + 0.1]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Cylinder>
            {/* Main breaker */}
            <Box args={[width * 0.3, height * 0.15, 0.1]} position={[0, height * 0.1, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#e53e3e" />
            </Box>
            {/* Conduits */}
            <Cylinder args={[0.1, 0.1, height * 0.8, 8]} position={[width * 0.4, 0, depth/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
            <Cylinder args={[0.1, 0.1, height * 0.8, 8]} position={[-width * 0.4, 0, depth/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
          </group>
        )
      
      case 'water_heater':
        // Water heater: Cylindrical tank with connections
        return (
          <group>
            {/* Main tank */}
            <Cylinder args={[width/2, width/2, height, 16]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" metalness={0.1} roughness={0.8} />
            </Cylinder>
            {/* Insulation jacket */}
            <Cylinder args={[width/2 + 0.1, width/2 + 0.1, height * 0.8, 16]} position={[0, 0, 0]} castShadow>
              <meshStandardMaterial color="#f7fafc" transparent opacity={0.8} />
            </Cylinder>
            {/* Control valve */}
            <Box args={[0.3, 0.3, 0.3]} position={[0, height/2 - 0.5, width/2]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Temperature gauge */}
            <Cylinder args={[0.15, 0.15, 0.1, 16]} position={[width/3, height/3, width/2]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Cylinder>
            {/* Water connections */}
            <Cylinder args={[0.1, 0.1, 0.8, 8]} position={[width/3, height/2 - 0.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#4299e1" />
            </Cylinder>
            <Cylinder args={[0.1, 0.1, 0.8, 8]} position={[-width/3, height/2 - 0.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#e53e3e" />
            </Cylinder>
            {/* Gas line (if gas unit) */}
            <Cylinder args={[0.08, 0.08, 0.6, 8]} position={[0, -height/2 + 0.3, width/3]} rotation={[Math.PI/2, 0, 0]} castShadow>
              <meshStandardMaterial color="#f6ad55" />
            </Cylinder>
          </group>
        )
      
      case 'septic_tank':
        // Septic tank: Underground concrete tank
        return (
          <group>
            {/* Main tank body */}
            <Box args={[width, height, depth]} position={[0, -height/2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#718096" roughness={0.9} />
            </Box>
            {/* Access covers */}
            <Cylinder args={[0.8, 0.8, 0.2, 16]} position={[-width/3, 0.1, 0]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            <Cylinder args={[0.8, 0.8, 0.2, 16]} position={[width/3, 0.1, 0]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            {/* Inlet pipe */}
            <Cylinder args={[0.15, 0.15, 2, 8]} position={[-width/2 - 1, -height/4, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Cylinder>
            {/* Outlet pipe */}
            <Cylinder args={[0.15, 0.15, 2, 8]} position={[width/2 + 1, -height/4, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Cylinder>
            {/* Vent pipe */}
            <Cylinder args={[0.1, 0.1, 3, 8]} position={[0, 1.5, 0]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
          </group>
        )
      
      case 'generator':
        // Backup generator: Rectangular housing with exhaust
        return (
          <group>
            {/* Main generator body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.7} roughness={0.3} />
            </Box>
            {/* Control panel */}
            <Box args={[width * 0.4, height * 0.3, 0.1]} position={[0, height * 0.25, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            {/* Display screen */}
            <Box args={[width * 0.25, height * 0.15, 0.05]} position={[0, height * 0.25, depth/2 + 0.075]} castShadow>
              <meshStandardMaterial color="#000000" emissive="#00ff00" emissiveIntensity={0.3} />
            </Box>
            {/* Exhaust pipe */}
            <Cylinder args={[0.2, 0.2, 1.5, 8]} position={[-width/3, height/2 + 0.75, -depth/3]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Cylinder>
            {/* Fuel connection */}
            <Cylinder args={[0.1, 0.1, 0.5, 8]} position={[width/2, -height/3, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#f6ad55" />
            </Cylinder>
            {/* Cooling vents */}
            {Array.from({ length: 6 }, (_, i) => (
              <Box 
                key={i} 
                args={[width * 0.8, 0.05, 0.05]} 
                position={[0, height * 0.1 - i * 0.1, depth/2 + 0.02]} 
                castShadow
              >
                <meshStandardMaterial color="#718096" />
              </Box>
            ))}
          </group>
        )
      
      case 'solar_panel':
        // Solar panel: Flat panel with frame
        return (
          <group>
            {/* Panel frame */}
            <Box args={[width, 0.1, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} roughness={0.2} />
            </Box>
            {/* Solar cells */}
            <Box args={[width * 0.95, 0.05, depth * 0.95]} position={[0, 0.075, 0]} castShadow>
              <meshStandardMaterial color="#1a202c" metalness={0.9} roughness={0.1} />
            </Box>
            {/* Cell grid lines */}
            {Array.from({ length: 6 }, (_, i) => (
              <Box 
                key={`h-${i}`} 
                args={[width * 0.9, 0.01, 0.02]} 
                position={[0, 0.08, -depth * 0.4 + i * (depth * 0.8 / 5)]} 
                castShadow
              >
                <meshStandardMaterial color="#4a5568" />
              </Box>
            ))}
            {Array.from({ length: 10 }, (_, i) => (
              <Box 
                key={`v-${i}`} 
                args={[0.02, 0.01, depth * 0.9]} 
                position={[-width * 0.4 + i * (width * 0.8 / 9), 0.08, 0]} 
                castShadow
              >
                <meshStandardMaterial color="#4a5568" />
              </Box>
            ))}
            {/* Junction box */}
            <Box args={[0.3, 0.15, 0.2]} position={[0, -0.075, -depth/2 + 0.1]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
          </group>
        )
      
      case 'ev_charger':
        // EV charger: Wall-mounted charging station
        return (
          <group>
            {/* Main charger body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#1a202c" metalness={0.8} roughness={0.2} />
            </Box>
            {/* Display screen */}
            <Box args={[width * 0.6, height * 0.3, 0.05]} position={[0, height * 0.2, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#000000" emissive="#0099ff" emissiveIntensity={0.4} />
            </Box>
            {/* Charging cable */}
            <Cylinder args={[0.1, 0.1, 2, 8]} position={[0, -height/4, depth/2 + 1]} rotation={[Math.PI/4, 0, 0]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Cylinder>
            {/* Charging connector */}
            <Box args={[0.3, 0.2, 0.4]} position={[0, -height/2, depth/2 + 1.8]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Box>
            {/* Status LED */}
            <Sphere args={[0.08]} position={[0, height * 0.35, depth/2 + 0.04]} castShadow>
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.6} />
            </Sphere>
            {/* Cable management */}
            <Cylinder args={[0.2, 0.2, 0.3, 8]} position={[width/3, -height/3, depth/2]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Cylinder>
          </group>
        )
      
      default:
        // Generic equipment box
        return (
          <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
            <meshStandardMaterial color={materials.primary} />
          </Box>
        )
    }
  }

  return (
    <group 
      position={position} 
      rotation={rotation} 
      scale={isDragging ? [scale[0] * 1.05, scale[1] * 1.05, scale[2] * 1.05] : scale} 
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      {/* Selection outline */}
      {isSelected && (
        <Box
          args={[dimensions.width + 0.5, dimensions.height + 0.5, dimensions.depth + 0.5]}
          position={[0, 0, 0]}
        >
          <meshBasicMaterial 
            color={isDragging ? "#ff6b00" : "#00ff00"} 
            wireframe 
            transparent 
            opacity={isDragging ? 0.7 : 0.5} 
          />
        </Box>
      )}
      
      {/* Equipment model */}
      <group position={[0, dimensions.height/2, 0]}>
        {renderEquipmentShape()}
      </group>
      
      {/* Equipment label */}
      {isSelected && (
        <group position={[0, dimensions.height + 0.5, 0]}>
          {/* Label background */}
          <Box args={[2.5, 0.3, 0.1]} position={[0, 0, 0]}>
            <meshBasicMaterial color="#000000" transparent opacity={0.7} />
          </Box>
        </group>
      )}
    </group>
  )
}

export default ADUEquipmentModel
